import React from 'react';
import styles from "./styles.module.scss";
import isEqual from 'lodash/isEqual';
import { Tooltip } from 'components/tool-tip';
import { setAssistantFlow } from 'helpers/assistantFlow';
import { OPTION_TOOLTIPS } from './option-tooltips';
import { WELCOME_OPTIONS } from './welcome-options';
import type { ConversationOption } from '../chat-messages/types';

interface OptionCardProps {
  title: string;
  value: string;
  /** When set, renders the larger welcome-card variant instead of the inline pill. */
  description?: string;
  Icon?: React.FunctionComponent<
      React.SVGProps<SVGSVGElement> & {
        title?: string | undefined;
      }
    >;
  hasArrow?: boolean;
  onClick: (value: string) => void;
}

const OptionCard: React.FC<OptionCardProps> = ({ title, value, description, Icon, hasArrow = false, onClick }) => {
  const card = (
    <div
      className={`${styles.optionCard} ${description ? styles.detailedCard : ''}`}
      onClick={() => onClick(value)}
      {...(value === 'evaluate' ? { 'data-tour-id': 'tour-cv-analysis-option' } : {})}
      {...(value === 'scratch' ? { 'data-tour-id': 'tour-hire-option' } : {})}
    >
      {description ? (
        <div className={styles.cardBody}>
          <p className={styles.cardTitle}>{title}</p>
          <p className={styles.cardDescription}>{description}</p>
        </div>
      ) : (
        <>
          <p className={styles.optionText}>
            {title}
          </p>
          {Icon && <div className={styles.optionIcon}>{<Icon/>}</div>}
          {hasArrow && <span className={styles.arrow}>→</span>}
        </>
      )}
    </div>
  );

  // Inline options in the talent audit flow explain themselves on hover. Options
  // without an entry (all of flow_1/flow_2) render exactly as before.
  const tooltip = OPTION_TOOLTIPS[value];
  if (!tooltip || description) return card;

  return (
    <Tooltip content={tooltip} tone="dark" className={styles.tooltipTrigger}>
      {card}
    </Tooltip>
  );
};

interface RecruitmentOptionsProps {
  onOptionSelect: (option: string) => void;
  options?: ConversationOption[] | null;
  containerClass?: string;
}

const RecruitmentOptions: React.FC<RecruitmentOptionsProps> = ({ onOptionSelect, options, containerClass }) => {
  const defaultOptions: ConversationOption[] = WELCOME_OPTIONS;

  const displayOptions = options || defaultOptions;

  const handleOptionClick = (option: string) => {
    // Store flow type in local storage
    if (option === "evaluate") {
      setAssistantFlow("flow_1");
    } else if (option === "scratch") {
      setAssistantFlow("flow_2");
    } else if (option === "talent_audit") {
      setAssistantFlow("flow_talent_audit");
    }

    onOptionSelect(option);
  };

  const shouldHaveArrow = (text: string): boolean => {
    return text.includes("→") ||
      text.includes("who can fit") ||
      text.includes("Not Sure Yet");
  };

  const isUsingDefault = isEqual(displayOptions, defaultOptions);
  const hasDetailedCards = displayOptions.some((option) => !!option.description);

  return (
    <div className={`${styles.recruitmentContainer} ${hasDetailedCards ? styles.hasDetailedCards : ''} ${containerClass}`}>
      <div
        className={`${styles.optionsGrid} ${isUsingDefault ? styles.isGrid : styles.isFlex}`}
      >
        {displayOptions.map((option, index) => (
          <OptionCard
            key={`option-${index}`}
            title={option.text}
            value={option.value}
            description={option.description}
            Icon={option.Icon}
            hasArrow={shouldHaveArrow(option.text)}
            onClick={handleOptionClick}
          />
        ))}
      </div>


    </div>
  );
};

export { RecruitmentOptions };